import React, { Component, ErrorInfo, ReactNode } from 'react'
import { ViewProps } from 'react-native'

import { ErrorView } from './ErrorView'

interface Props extends ViewProps {
  children: ReactNode
  catchErrors: 'always' | 'dev' | 'prod' | 'never'
}

interface State {
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * This component handles whenever the user encounters a JS error in the
 * app. It follows the "error boundary" pattern in React. We're using a
 * class component because according to the documentation, only class
 * components can be error boundaries.
 *
 * Read more here:
 *
 * @link: https://reactjs.org/docs/error-boundaries.html
 */
interface ErrorBoundaryState {
  error: Error | null
  errorInfo: {
    componentStack: string
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: ErrorBoundaryState = { error: null, errorInfo: { componentStack: '' } }

  // If an error in a child is encountered, this will run
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)

    // Catch errors in any components below and re-render with error message
    if (error?.message !== this.state.error?.message) {
      this.setState({
        error,
        errorInfo
      })
      // if (!__DEV__) {
      //   Promise.allSettled([crashlytics().log(error.message)]).then(() => {
      //     console.log('[ErrorBoundary] crashlytics captured error')
      //   })
      // }
    }

    // You can also log error messages to an error reporting service here
    // This is a great place to put BugSnag, Sentry, Honeybadger, etc:
    // reportErrorToCrashReportingService(error)
  }

  // Reset the error back to null
  resetError = () => {
    this.setState({ error: null, errorInfo: null })
  }

  // Re-render when error state changes or children change
  shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>): boolean {
    // Always re-render if error state changed
    if (nextState.error !== this.state.error) {
      return true
    }
    // Always re-render if children changed
    if (nextProps.children !== this.props.children) {
      return true
    }
    return false
  }

  // Only enable if we're catching errors in the right environment
  isEnabled(): boolean {
    return (
      this.props.catchErrors === 'always' ||
      (this.props.catchErrors === 'dev' && __DEV__) ||
      (this.props.catchErrors === 'prod' && !__DEV__)
    )
  }

  // Render an error UI if there's an error; otherwise, render children
  render() {
    return this.isEnabled() && this.state.error ? (
      <ErrorView onReset={this.resetError} error={this.state.error} errorInfo={this.state.errorInfo} />
    ) : (
      this.props.children
    )
  }
}
